import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, ArrowRight, CreditCard, Landmark, ShieldCheck, Package, Gift, X } from 'lucide-react';
import DisclaimerBox from '../components/DisclaimerBox';
import { AddOns } from '../components/AddOns';
import KitNudge from '../components/KitNudge';
import { useCart } from '../context/CartContext';
import { AU_STATES } from '../mock/mock';
import { createOrder, validateDiscount, getSettings, customerMe, getCustomerToken, checkoutIntent, customerSetBirthday } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const EMPTY = { fullName: '', email: '', phone: '', address: '', city: '', state: '', postcode: '', country: 'Australia', notes: '' };

const AU_POSTCODE_RANGES = {
  NSW: [[1000, 1999], [2000, 2599], [2619, 2899], [2921, 2999]],
  ACT: [[200, 299], [2600, 2618], [2900, 2920]],
  VIC: [[3000, 3999], [8000, 8999]],
  QLD: [[4000, 4999], [9000, 9999]],
  SA: [[5000, 5799], [5800, 5999]],
  WA: [[6000, 6797], [6800, 6999]],
  TAS: [[7000, 7799], [7800, 7999]],
  NT: [[800, 899], [900, 999]],
};

const postcodeMatchesState = (pc, state) => {
  if (!/^[0-9]{4}$/.test(pc) || !state) return false;
  const n = parseInt(pc, 10);
  return (AU_POSTCODE_RANGES[state] || []).some(([lo, hi]) => n >= lo && n <= hi);
};

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, shipping, total, hasPreorder, hasPhysical, clearCart } = useCart();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [checks, setChecks] = useState({ research: false, terms: false, refund: false });
  const [marketing, setMarketing] = useState({ newStock: false, specials: false });
  const [processing, setProcessing] = useState(false);
  const [settings, setSettings] = useState(null);
  const clientRef = useRef((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
  const [discountInput, setDiscountInput] = useState('');
  const [discount, setDiscount] = useState(null); // { code, amount }
  const [discountError, setDiscountError] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [credit, setCredit] = useState(0);
  const [useCredit, setUseCredit] = useState(false);
  const [payMethod, setPayMethod] = useState('bank'); // 'bank' | 'card' | 'afterpay'
  const [me, setMe] = useState(null);
  const [bday, setBday] = useState({ day: '', month: '' });
  const [bdayBusy, setBdayBusy] = useState(false);
  const [bdayHidden, setBdayHidden] = useState(false);

  useEffect(() => {
    if (getCustomerToken()) customerMe().then((m) => { setMe(m); setCredit(Number(m.credit || 0)); }).catch(() => {});
  }, []);

  const saveBirthday = async () => {
    if (!bday.day || !bday.month) { toast({ title: 'Pick your day and month', variant: 'destructive' }); return; }
    setBdayBusy(true);
    try {
      const res = await customerSetBirthday(Number(bday.day), Number(bday.month));
      setMe((m) => ({ ...m, birthday: res.birthday }));
      toast({ title: 'Birthday saved 🎁', description: 'A treat is coming your way on the day.' });
    } catch (e) { toast({ title: 'Could not save birthday', variant: 'destructive' }); }
    finally { setBdayBusy(false); }
  };

  useEffect(() => {
    if (settings?.cardPaymentsPaused && payMethod !== 'bank') setPayMethod('bank');
  }, [settings, payMethod]);

  const discountAmount = discount ? discount.amount : 0;  const instockSubtotal = items.filter((i) => i.stockStatus === 'In Stock').reduce((a, i) => a + i.price * i.qty, 0);
  const freeThreshold = settings?.freeShippingThreshold ?? 300;
  const shipEnabled = settings?.shippingEnabled ?? true;
  const qualifiesFree = freeThreshold > 0 && items.some((i) => i.stockStatus === 'In Stock') && instockSubtotal >= freeThreshold;
  const baseShip = (!shipEnabled || qualifiesFree || !hasPhysical) ? 0 : (settings?.standardShipping ?? 13);
  const shipCost = !hasPhysical ? 0 : Number(baseShip) + Number(settings?.handlingFee ?? 0);
  const totalBeforeCredit = Math.max(0, subtotal - discountAmount) + shipCost;
  const cardEnabled = settings?.cardPaymentEnabled && settings?.cardPaymentLink;
  const paused = !!settings?.cardPaymentsPaused;
  const afterpayEnabled = cardEnabled && settings?.afterpayEnabled;
  const cardPct = settings?.cardSurchargePercent ?? 0;
  const apPct = settings?.afterpaySurchargePercent ?? 0;
  const apFixed = settings?.afterpayFixedFee ?? 0;
  let cardSurcharge = 0;
  let feeLabel = '';
  if (payMethod === 'card' && cardEnabled && cardPct > 0) {
    cardSurcharge = Math.round(totalBeforeCredit * cardPct) / 100;
    feeLabel = `Card fee (${cardPct}%)`;
  } else if (payMethod === 'afterpay' && afterpayEnabled && (apPct > 0 || apFixed > 0)) {
    cardSurcharge = Math.round((totalBeforeCredit * apPct / 100 + apFixed) * 100) / 100;
    feeLabel = `Afterpay fee (${apPct}% + $${apFixed.toFixed(2)})`;
  }
  const totalWithCard = totalBeforeCredit + cardSurcharge;
  const creditApplied = useCredit ? Math.min(credit, totalWithCard) : 0;
  const finalTotal = Math.max(0, totalWithCard - creditApplied);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) return;
    setApplyingCode(true);
    setDiscountError('');
    try {
      const giftTotal = items.filter((i) => i.giftCard).reduce((a, i) => a + i.price * i.qty, 0);
      const res = await validateDiscount(code, Math.max(0, subtotal - giftTotal), form.email);
      if (res.valid) {
        setDiscount({ code: res.code, amount: res.discountAmount, freeItem: res.freeItem || null });
        toast({ title: res.freeItem ? 'Free gift added' : 'Discount applied', description: res.freeItem ? `${res.code}: free ${res.freeItem.name} will ship with your order.` : `${res.code}: -$${res.discountAmount.toFixed(2)}` });
      } else {
        setDiscount(null);
        setDiscountError(res.message || 'Invalid discount code.');
      }
    } catch (e) {
      setDiscount(null);
      setDiscountError('Could not validate code. Please try again.');
    } finally {
      setApplyingCode(false);
    }
  };

  const removeDiscount = () => { setDiscount(null); setDiscountInput(''); setDiscountError(''); };

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  // Abandoned-checkout capture: quietly save intent once an email is typed.
  useEffect(() => {
    if (!form.email || !form.email.includes('@') || items.length === 0) return;
    const t = setTimeout(() => {
      checkoutIntent({
        email: form.email, fullName: form.fullName, phone: form.phone,
        itemCount: items.reduce((a, i) => a + i.qty, 0), total,
        items: items.map((i) => `${i.qty}x ${i.name}`).join(', '),
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, form.fullName, form.phone, items.length]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif text-4xl text-alp-cream mb-4">Your Cart is Empty</h1>
        <Link to="/shop" className="text-alp-gold">Browse Catalog</Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const postcodeValid = postcodeMatchesState(form.postcode, form.state);
  const requiredFilled =
    form.fullName && form.email && form.phone && form.address &&
    form.city && form.state && postcodeValid;
  const allChecked = checks.research && checks.terms && checks.refund;
  const canPay = requiredFilled && allChecked;

  const buildPayload = () => ({
    items: items.filter((i) => !i.giftCard).map((i) => ({ id: i.id, qty: i.qty })),
    giftCards: items.filter((i) => i.giftCard).flatMap((i) => Array.from({ length: i.qty }, () => ({ amount: i.price, ...i.giftCard }))),
    customer: form,
    confirmations: checks,
    discountCode: discount ? discount.code : null,
    clientRef: clientRef.current,
    marketing,
    useCredit,
    paymentMethod: payMethod === 'card' ? 'Card' : payMethod === 'afterpay' ? 'Afterpay' : 'Bank Transfer',
  });

  const handleSuccess = (order) => {
    clearCart();
    navigate(`/order-confirmation?order=${order.orderNumber}`);
  };

  const handlePlaceOrder = async () => {
    if (!canPay) {
      toast({ title: 'Please complete all fields', description: 'All details and confirmations are required before placing your order.', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      const order = await createOrder(buildPayload());
      if ((payMethod === 'card' || payMethod === 'afterpay') && cardEnabled) {
        // Open Square card payment in a new tab, then send to confirmation with card instructions.
        window.open(settings.cardPaymentLink, '_blank', 'noopener');
      }
      handleSuccess(order);
    } catch (e) {
      toast({ title: 'Order failed', description: e?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
      setProcessing(false);
    }
  };

  return (
    <div className="bg-alp-black min-h-screen aurora-wash relative overflow-hidden">
      <div aria-hidden className="gold-orb w-72 h-72 -left-16 top-56 hidden lg:block" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-14">
        <h1 className="font-serif text-5xl text-alp-cream mb-3 heading-glow">Checkout</h1>
        <p className="text-alp-gray mb-10">Australia only &middot; Pay by bank transfer, card or Afterpay in AUD</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* FORM */}
          <div className="lg:col-span-3 space-y-8">
            <div className="border border-alp-line rounded p-6 bg-alp-card/40">
              <h2 className="font-serif text-2xl text-alp-cream mb-6">Shipping Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full Name" value={form.fullName} onChange={set('fullName')} testid="checkout-fullname" />
                <Field label="Email" type="email" value={form.email} onChange={set('email')} testid="checkout-email" />
                <Field label="Phone" type="tel" inputMode="tel" value={form.phone} onChange={set('phone')} testid="checkout-phone" />

                <div className="sm:col-span-2 pt-2">
                  <Field label="Street Address" value={form.address} onChange={set('address')} testid="checkout-address" />
                </div>
                <Field label="Suburb" value={form.city} onChange={set('city')} testid="checkout-suburb" />
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">State</Label>
                  <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                    <SelectTrigger data-testid="checkout-state" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                      {AU_STATES.map((s) => (<SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Field label="Postcode" value={form.postcode} onChange={set('postcode')} placeholder="4 digits" testid="checkout-postcode" />
                  {form.postcode && form.state && !postcodeValid && (
                    <p data-testid="checkout-postcode-error" className="text-[11px] text-red-400 mt-1.5">Postcode does not match the selected state.</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Country</Label>
                  <Input value="Australia" readOnly disabled data-testid="checkout-country" className="bg-alp-dark border-alp-line text-alp-cream/70 cursor-not-allowed" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Order Notes (optional)</Label>
                  <Textarea value={form.notes} onChange={set('notes')} className="bg-alp-dark border-alp-line text-alp-cream" rows={3} />
                </div>
              </div>
              <p className="text-xs text-alp-gray/60 mt-4">We ship to Australian addresses only. International orders cannot be processed.</p>
            </div>

            <div className="border border-alp-line rounded p-6 bg-alp-card/40">
              <h2 className="font-serif text-2xl text-alp-cream mb-5">Confirmations</h2>
              <div className="space-y-4">
                <CheckRow checked={checks.research} onChange={(v) => setChecks((c) => ({ ...c, research: v }))} label="I confirm these products are for research purposes only and not for human consumption." />
                <CheckRow checked={checks.terms} onChange={(v) => setChecks((c) => ({ ...c, terms: v }))} label={<>I agree to the <Link to="/terms" className="text-alp-gold underline">Terms &amp; Conditions</Link>.</>} />
                <CheckRow checked={checks.refund} onChange={(v) => setChecks((c) => ({ ...c, refund: v }))} label="I understand there are no refunds or returns once payment is completed." />
                <div className="pt-3 mt-1 border-t border-alp-line/50">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-2">Keep me posted (optional)</p>
                  <CheckRow checked={marketing.newStock} onChange={(v) => setMarketing((m) => ({ ...m, newStock: v }))} label="Email me when new stock arrives." testid="opt-newstock" />
                  <CheckRow checked={marketing.specials} onChange={(v) => setMarketing((m) => ({ ...m, specials: v }))} label="Email me about specials & sales." testid="opt-specials" />
                </div>
              </div>
            </div>
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-2 space-y-4">
            <AddOns excludeCode="KIT10" />
            <div className="border border-alp-line rounded p-6 bg-alp-card/40 sticky top-28">
              <h2 className="font-serif text-2xl text-alp-cream mb-6">Order Summary</h2>
              <KitNudge />
              <div className="space-y-3 mb-5">
                {items.map((i) => (
                  <div key={i.id} className="flex justify-between text-sm">
                    <span className="text-alp-cream/80">{i.name} <span className="text-alp-gray">&times;{i.qty}</span></span>
                    <span className="text-alp-cream">${(i.price * i.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="gold-divider my-4" />
              <div className="mb-4">
                {!discount ? (
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Discount Code</Label>
                    <div className="flex gap-2">
                      <Input value={discountInput} onChange={(e) => setDiscountInput(e.target.value.toUpperCase())} placeholder="e.g. LUXE10" data-testid="discount-code-input" className="bg-alp-dark border-alp-line text-alp-cream uppercase" />
                      <button onClick={applyDiscount} disabled={applyingCode || !discountInput.trim()} data-testid="apply-discount-btn" className="shrink-0 px-4 text-xs tracking-[0.15em] uppercase border border-alp-gold/60 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                        {applyingCode ? '...' : 'Apply'}
                      </button>
                    </div>
                    {discountError && <p data-testid="discount-error" className="text-[11px] text-red-400 mt-1.5">{discountError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between border border-alp-gold/40 rounded px-3 py-2" data-testid="discount-applied">
                    <span className="text-sm text-alp-gold">{discount.freeItem ? <>{discount.code} applied &mdash; free {discount.freeItem.name} {discount.freeItem.size}</> : <>{discount.code} applied (&minus;${discountAmount.toFixed(2)})</>}</span>
                    <button onClick={removeDiscount} data-testid="remove-discount-btn" className="text-xs text-alp-gray hover:text-red-400">Remove</button>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-alp-gray"><span>Subtotal</span><span className="text-alp-cream">${subtotal.toFixed(2)}</span></div>
                {discount && !discount.freeItem && (
                  <div className="flex justify-between text-alp-gold"><span>Discount ({discount.code})</span><span data-testid="summary-discount">&minus;${discountAmount.toFixed(2)}</span></div>
                )}
                {discount && discount.freeItem && (
                  <div className="flex justify-between text-alp-gold"><span>Free gift ({discount.freeItem.name})</span><span data-testid="summary-free-gift">FREE</span></div>
                )}
                {credit > 0 && (
                  <label className="flex items-center justify-between gap-2 border border-alp-gold/30 rounded px-3 py-2.5 cursor-pointer select-none" data-testid="use-credit-row">
                    <span className="flex items-center gap-2 text-alp-cream/85 text-sm">
                      <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} data-testid="use-credit-checkbox" className="accent-alp-gold" />
                      Use my store credit (${credit.toFixed(2)} available)
                    </span>
                    {useCredit && <span className="text-alp-gold text-sm" data-testid="summary-credit">&minus;${creditApplied.toFixed(2)}</span>}
                  </label>
                )}
                <div className="flex justify-between text-alp-gray"><span>Express Post</span><span className="text-alp-cream">{shipCost === 0 ? 'FREE' : `$${shipCost.toFixed(2)}`}</span></div>
                {cardSurcharge > 0 && (
                  <div className="flex justify-between text-alp-gray"><span>{feeLabel}</span><span className="text-alp-cream" data-testid="summary-card-fee">+${cardSurcharge.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-lg pt-2"><span className="text-alp-cream">Total</span><span className="text-alp-gold" data-testid="summary-total">${finalTotal.toFixed(2)} AUD</span></div>
              </div>

              {me && !me.birthday && !bdayHidden && (
                <div className="border border-alp-gold/30 rounded px-4 py-3 mt-5 bg-alp-gold/5" data-testid="checkout-birthday-nudge">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-alp-cream/85 text-xs leading-relaxed flex items-center gap-1.5">
                      <Gift size={13} className="text-alp-gold shrink-0" /> Add your birthday &mdash; members get a little treat from us on their day
                    </p>
                    <button onClick={() => setBdayHidden(true)} data-testid="birthday-nudge-dismiss" aria-label="Dismiss" className="text-alp-gray hover:text-alp-gold shrink-0"><X size={13} /></button>
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    <select value={bday.day} onChange={(e) => setBday((b) => ({ ...b, day: e.target.value }))} data-testid="checkout-bday-day"
                      className="bg-alp-dark border border-alp-line rounded px-2 py-1.5 text-xs text-alp-cream focus:border-alp-gold outline-none">
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                    </select>
                    <select value={bday.month} onChange={(e) => setBday((b) => ({ ...b, month: e.target.value }))} data-testid="checkout-bday-month"
                      className="bg-alp-dark border border-alp-line rounded px-2 py-1.5 text-xs text-alp-cream focus:border-alp-gold outline-none">
                      <option value="">Month</option>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <button onClick={saveBirthday} disabled={bdayBusy} data-testid="checkout-bday-save"
                      className="px-3 py-1.5 bg-alp-gold text-alp-black rounded text-[10px] tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50">
                      {bdayBusy ? '...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              {hasPreorder && (
                <div className="hairline rounded px-4 py-3 mt-5 text-xs text-alp-cream/80 leading-relaxed">
                  In-stock and preorder items may ship separately. Preorder ETA is usually 14&ndash;21 days after the supplier order is placed. Weekly preorder cut-off: Sunday. Supplier order placed Monday.
                </div>
              )}

              {/* PAYMENT METHOD */}
              {cardEnabled && (
                <div className="mt-6" data-testid="payment-method-select">
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Payment Method</Label>
                  <div className={`grid ${afterpayEnabled ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
                    <button type="button" onClick={() => setPayMethod('bank')} data-testid="pay-method-bank"
                      className={`flex items-center justify-center gap-2 px-2 py-3 text-xs tracking-[0.1em] uppercase border rounded transition-colors ${payMethod === 'bank' ? 'border-alp-gold bg-alp-gold/10 text-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/50'}`}>
                      <Landmark size={15} /> Bank
                    </button>
                    <button type="button" onClick={() => !paused && setPayMethod('card')} disabled={paused} data-testid="pay-method-card"
                      className={`relative flex flex-col items-center justify-center gap-1 px-2 py-3 text-xs tracking-[0.1em] uppercase border rounded transition-colors ${paused ? 'border-alp-line/60 bg-alp-dark/60 text-alp-gray/40 cursor-not-allowed opacity-60' : payMethod === 'card' ? 'border-alp-gold bg-alp-gold/10 text-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/50'}`}>
                      <span className="flex items-center gap-2"><CreditCard size={15} /> Card</span>
                      {paused && <span className="text-[8px] tracking-[0.1em] normal-case" data-testid="card-paused-tag">Temporarily unavailable</span>}
                    </button>
                    {afterpayEnabled && (
                      <button type="button" onClick={() => !paused && setPayMethod('afterpay')} disabled={paused} data-testid="pay-method-afterpay"
                        className={`relative flex flex-col items-center justify-center gap-1 px-2 py-3 text-xs tracking-[0.1em] uppercase border rounded transition-colors ${paused ? 'border-alp-line/60 bg-alp-dark/60 text-alp-gray/40 cursor-not-allowed opacity-60' : payMethod === 'afterpay' ? 'border-alp-gold bg-alp-gold/10 text-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/50'}`}>
                        <span>Afterpay</span>
                        {paused && <span className="text-[8px] tracking-[0.1em] normal-case" data-testid="afterpay-paused-tag">Temporarily unavailable</span>}
                      </button>
                    )}
                  </div>
                  {paused && (
                    <p className="text-[11px] text-amber-300/90 mt-2" data-testid="payments-paused-note">Card &amp; Afterpay are temporarily unavailable &mdash; please checkout with Bank Transfer. We&rsquo;ll have them back shortly.</p>
                  )}
                  {payMethod === 'card' && (
                    <p className="text-[11px] text-alp-gray/80 mt-2" data-testid="card-fee-note">{cardPct > 0 ? `A ${cardPct}% bank fee applies to card payments. ` : 'No card surcharge \u2014 we cover the processing fee. '}You'll pay on our secure card page and must enter the <strong>amount and your order number</strong> in the payment description so we can match it.</p>
                  )}
                  {payMethod === 'afterpay' && afterpayEnabled && (
                    <p className="text-[11px] text-alp-gray/80 mt-2" data-testid="afterpay-fee-note">{(apPct > 0 || apFixed > 0) ? `Afterpay carries a higher processing fee (${apPct}% + $${apFixed.toFixed(2)}). ` : 'No Afterpay surcharge \u2014 we cover the processing fee. '}Choose Afterpay on our secure payment page and enter the <strong>amount and your order number</strong> in the payment description so we can match it.</p>
                  )}
                </div>
              )}

              <div className="mt-6">
                {!canPay && (
                  <div className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold bg-alp-line text-alp-gray cursor-not-allowed">
                    <Lock size={15} /> Complete Details to Order
                  </div>
                )}
                {canPay && (
                  <button onClick={handlePlaceOrder} disabled={processing} data-testid="place-order-btn" className={`w-full inline-flex items-center justify-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${processing ? 'bg-alp-line text-alp-gray cursor-not-allowed' : 'bg-alp-gold text-alp-black hover:bg-alp-goldlight'}`}>
                    {processing ? 'Placing Order\u2026' : (payMethod === 'card' && cardEnabled) ? <>Place Order &amp; Pay by Card <ArrowRight size={15} /></> : (payMethod === 'afterpay' && afterpayEnabled) ? <>Place Order &amp; Pay with Afterpay <ArrowRight size={15} /></> : <>Place Order &middot; Bank Transfer <ArrowRight size={15} /></>}
                  </button>
                )}
                <p className="text-center text-[11px] text-alp-gray/70 mt-3">
                  {(payMethod === 'card' && cardEnabled) || (payMethod === 'afterpay' && afterpayEnabled)
                    ? "You'll be taken to our secure payment page to complete your order total. Your order ships once payment is received."
                    : "Payment is by bank transfer. You'll get the bank details and an order number on the next screen. Your order ships once payment is received."}
                </p>
                <div className="glass-card rounded-lg px-4 py-3 mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2" data-testid="checkout-trust-strip">
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-alp-cream/75">
                    <Lock size={12} className="text-alp-gold" /> Secure
                  </span>
                  <span aria-hidden className="w-px h-3.5 bg-alp-gold/30" />
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-alp-cream/75">
                    <ShieldCheck size={12} className="text-alp-gold" /> Square Protected
                  </span>
                  <span aria-hidden className="w-px h-3.5 bg-alp-gold/30" />
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-alp-cream/75">
                    <Package size={12} className="text-alp-gold" /> Discreet Packaging
                  </span>
                  {afterpayEnabled && !paused && (
                    <>
                      <span aria-hidden className="w-px h-3.5 bg-alp-gold/30" />
                      <span className="bg-white/95 rounded px-2 py-1 inline-flex items-center" title="Afterpay available">
                        <img src="/afterpay-logo.png" alt="Afterpay accepted" className="h-3.5 w-auto" data-testid="afterpay-logo" />
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-6"><DisclaimerBox compact /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, testid, inputMode }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">{label}</Label>
      <Input type={type} inputMode={inputMode} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} className="bg-alp-dark border-alp-line text-alp-cream" />
    </div>
  );
}

function CheckRow({ checked, onChange, label, testid }) {
  return (
    <label className="flex gap-3 items-start cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-0.5 border-alp-gold/60 data-[state=checked]:bg-alp-gold data-[state=checked]:text-alp-black" />
      <span className="text-sm text-alp-cream/80 leading-relaxed">{label}</span>
    </label>
  );
}
